import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Form, Button, Alert, Row, Col, Card, Spinner, Modal } from 'react-bootstrap';
import { FaSignInAlt, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaKey } from 'react-icons/fa';
import apiClient from '../apis/apiClient';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetData, setResetData] = useState({ otp: '', newPassword: '', confirmPassword: '' });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!email.trim()) return setError('Email is required');
    if (!password) return setError('Password is required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Please enter a valid email address');

    setLoading(true);

    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const data = response.data;

      // Extract user data with multiple fallbacks
      const token = data.token || data.access_token;
      const userData = data.user || data;
      const userId = userData.id || userData.userId || '';
      const username = userData.name || userData.username || '';
      const userEmail = userData.email || email;
      const roles = userData.roles || data.roles || [];

      if (!token) {
        throw new Error('Authentication token not received');
      }

      // Store all user data in localStorage
      localStorage.setItem('authToken', token);
      localStorage.setItem('userId', userId);
      localStorage.setItem('userName', username);
      localStorage.setItem('userEmail', userEmail);
      localStorage.setItem('user', JSON.stringify({
        id: userId,
        name: username,
        email: userEmail,
        roles: roles
      }));
      localStorage.setItem('roles', JSON.stringify(roles));

      // Redirect based on role
      if (roles.includes('ROLE_ADMIN')) {
        navigate('/dashboard');
      } else {
        navigate('/profile');
      }

    } catch (err) {
      console.error('Login Error:', err);
      // Clear potentially incomplete data
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('user');
      localStorage.removeItem('roles');

      setError(err.response?.data?.message || err.message || 'An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!forgotPasswordEmail.trim()) return setError('Email is required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) return setError('Please enter a valid email address');

    setLoading(true);

    try {
      const response = await apiClient.post('/auth/forgot-password', { email: forgotPasswordEmail });
      const data = response.data;

      setSuccess(data.message || 'OTP has been sent to your email address');
      setShowForgotPasswordModal(false);
      setShowResetPasswordModal(true);
    } catch (err) {
      console.error('Forgot Password Error:', err);
      setError(err.response?.data?.message || err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!resetData.otp) return setError('OTP is required');
    if (!resetData.newPassword) return setError('New password is required');
    if (resetData.newPassword !== resetData.confirmPassword) return setError('Passwords do not match');
    if (resetData.newPassword.length < 6) return setError('Password must be at least 6 characters');

    setLoading(true);

    try {
      const response = await apiClient.post('/auth/reset-password', {
        otp: resetData.otp,
        password: resetData.newPassword
      });
      const data = response.data;

      setSuccess(data.message || 'Password has been reset successfully. You can now login with your new password.');
      setShowResetPasswordModal(false);
      setResetData({ otp: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      console.error('Reset Password Error:', err);
      setError(err.response?.data?.message || err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="login-container">
      <Row className="justify-content-center align-items-center min-vh-100">
        <Col md={8} lg={6} xl={5}>
          <Card className="login-card shadow-lg border-0">
            <Card.Body className="p-4 p-md-5">
              <div className="text-center mb-4">
                <h2 className="fw-bold mb-2 text-primary">Willkommen in Papadam</h2>
                <p className="text-muted">Melden Sie sich an, um auf Ihr Konto zuzugreifen</p>
              </div>

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              <Form onSubmit={handleLogin} className="mt-4">
                <Form.Group className="mb-3">
                  <Form.Label>E-Mail-Adresse</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text bg-light">
                      <FaEnvelope className="text-muted" />
                    </span>
                    <div className="custom-input-group">
                      <Form.Control
                        type="email"
                        placeholder="Geben Sie Ihre E-Mail-Adresse ein"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="custom-email-input"
                      />
                    </div>

                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Passwort</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text bg-light">
                      <FaLock className="text-muted" />
                    </span>
                    <div className="custom-input-group">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder="Geben Sie Ihr Passwort ein"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength="6"
                        className="custom-password-input"
                      />
                    </div>

                    <button
                      type="button"
                      className="input-group-text bg-light"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <div className="d-flex justify-content-between mt-2">
                    <Form.Check
                      type="checkbox"
                      label="Erinnere dich an mich"
                      className="small"
                    />

                    <Link to="/forgot-password" className="small text-decoration-none">
                      Passwort vergessen?
                    </Link>

                  </div>
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 py-2 mt-3 fw-bold"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Anmelden...
                    </>
                  ) : (
                    <>
                      <FaSignInAlt className="me-2" />
                      Anmelden
                    </>
                  )}
                </Button>

                <div className="text-center mt-4 pt-3 border-top">
                  <p className="text-muted mb-0">
                    Ich habe noch kein Konto ?{' '}
                    <Link to="/register" className="text-decoration-none fw-bold">
                      Erstellen Sie eins
                    </Link>
                  </p>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Forgot Password Modal */}
      <Modal show={showForgotPasswordModal} onHide={() => setShowForgotPasswordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleForgotPassword}>
          <Modal.Body>
            <p>Enter your email address and we'll send you an OTP to reset your password.</p>
            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <FaEnvelope className="text-muted" />
                </span>
                <Form.Control
                  type="email"
                  placeholder="Enter your email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                />
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowForgotPasswordModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Send OTP'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showResetPasswordModal} onHide={() => setShowResetPasswordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Set New Password</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleResetPassword}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>OTP</Form.Label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <FaKey className="text-muted" />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Enter OTP"
                  value={resetData.otp}
                  onChange={(e) => setResetData({ ...resetData, otp: e.target.value })}
                  required
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <FaLock className="text-muted" />
                </span>
                <Form.Control
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={resetData.newPassword}
                  onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                  required
                  minLength="6"
                />
                <button
                  type="button"
                  className="input-group-text bg-light"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <FaLock className="text-muted" />
                </span>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={resetData.confirmPassword}
                  onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                  required
                  minLength="6"
                />
                <button
                  type="button"
                  className="input-group-text bg-light"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowResetPasswordModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Reset Password'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Login;  
